import { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import {
  useDeletAppraisalPeriodMutation,
  useGetAppraisalPeriodQuery,
} from "../../../../api/hrApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import { CTAButtons } from "../../../../global/components/Buttons/buttons";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import DeleteItemModal from "../../../Components/Modals/DeleteItemModal";
import { AppModalTemplate } from "../../../Components/Modals/Modals";
import Pagination from "../../../Components/Pagination/Pagination";
import {
  CalendarFilter2,
  FilterButton,
  ProDropFilter,
  SearchFilter,
} from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";
import { getStatusText } from "../../Finance/CostCenter/Class";
import AppraisalPeriodModal from "./Modals/AppraisalPeriodModal";
import EditAppraisalPeriodModal from "./Modals/EditAppraisalPeriodModal";
import { FilterByStatus } from "./status";
const AppraisalPeriod = () => {
  const [onDelete] = useDeletAppraisalPeriodMutation();
  const [openModal, setOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openCreatModal, setOpenCreatModal] = useState(false);
  const [yearFilter, setYearFilter] = useState("All Years");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const handleOpenCreateModale = () => {
    setOpenCreatModal(!openCreatModal);
  };
  const navigate = useNavigate();
  const handleModal = () => {
    setOpenModal(!openModal);
  };
  const {
    searchText,
    filter,
    setSearchText,
    setFilter,
    pageSize,
    setPageSize,
    setCurrentPage,
    currentPage,
  } = useSearchFilter({
    initialFilter: 1,
  });
  const { data, isLoading } = useGetAppraisalPeriodQuery({
    GeneralSearch: searchText,
    PageNumber: currentPage,
    PageSize: pageSize,
    ApprovalStatus: filter,
    year: yearFilter === "All Years" ? "" : yearFilter || "",
  });
  const handleOpenDeleteModal = (id) => {
    if (id) {
      setSelectedId(id);
      setOpenDelete(true);
    }
  };
  const handleCloseDelete = () => {
    setSelectedId(null);
    setOpenDelete(false);
  };
  const handleDelete = async () => {
    try {
      const response = await onDelete({
        id: parseInt(selectedId),
      }).unwrap();
      if (response?.successful) {
        handleCloseDelete();
        toast.success(response?.statusMessage);
      } else {
        toast.error(response?.data?.title);
      }
    } catch (error) {
      toast.error(error.message ?? "Error deleting Counselling Type");
    }
  };
  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const handleEdit = (id) => {
    if (id) {
      setSelectedId(id);
      handleModal();
    }
  };
  const actionButton = (
    <>
      <CTAButtons onClick={handleOpenCreateModale}>
        Create Appraisal Period
      </CTAButtons>
    </>
  );
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const currentYear = new Date().getFullYear();
  const dates = [
    { name: "All Years", filter: "" },
    ...Array.from({ length: currentYear - 1999 + 1 }, (_, i) => ({
      name: (currentYear - i).toString(),
      filter: (currentYear - i).toString(),
    })),
  ];

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data?.responseObject?.result) return [];
    return [...data.responseObject.result].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      if (sortColumn === "startMonth" || sortColumn === "endMonth") {
        aValue = months[aValue - 1];
        bValue = months[bValue - 1];
      } else if (sortColumn === "approvalStatus") {
        aValue = typeof aValue === "number" ? getStatusText(aValue) : aValue;
        bValue = typeof bValue === "number" ? getStatusText(bValue) : bValue;
      } else if (sortColumn === "createdDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortColumn === "currentYear") {
        aValue = aValue ? "True" : "False";
        bValue = bValue ? "True" : "False";
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data?.responseObject?.result, sortColumn, sortDirection, months]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  return (
    <PageStyle
      action={actionButton}
      hasBack={true}
      backURL={"/dashboard/hr/HR-Appraisal-Setup"}
      support={
        " Creates Period for the year which will then be used when creating an objective."
      }
      demoTitle={"Manage Appraisal Period"}
      hasTopPageDemo
    >
      <form className={DashboardStyle.dashboard_filter}>
        <SearchFilter
          value={searchText}
          text={(searchText) => setSearchText(searchText)}
        />
        <ProDropFilter
          filter={filter}
          setFilter={setFilter}
          name={"Status"}
          filterBy={FilterByStatus}
        />
        <ProDropFilter
          filter={yearFilter}
          setFilter={setYearFilter}
          name={"Year"}
          filterBy={dates}
        />
      </form>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>S/N</th>
              <th
                onClick={() => handleSort("year")}
                style={{ cursor: "pointer" }}
              >
                Year {renderSortIcon("year")}
              </th>
              <th
                onClick={() => handleSort("startMonth")}
                style={{ cursor: "pointer" }}
              >
                Start Month {renderSortIcon("startMonth")}
              </th>
              <th
                onClick={() => handleSort("endMonth")}
                style={{ cursor: "pointer" }}
              >
                End Month {renderSortIcon("endMonth")}
              </th>
              <th
                onClick={() => handleSort("currentYear")}
                style={{ cursor: "pointer" }}
              >
                Current Year {renderSortIcon("currentYear")}
              </th>
              <th
                onClick={() => handleSort("approvalStatus")}
                style={{ cursor: "pointer" }}
              >
                Approval Status {renderSortIcon("approvalStatus")}
              </th>
              <th
                onClick={() => handleSort("createdDate")}
                style={{ cursor: "pointer" }}
              >
                Created Date {renderSortIcon("createdDate")}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={8} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {sortedData?.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item?.year}</td>
                <td>{months[item?.startMonth - 1]}</td>
                <td>{months[item?.endMonth - 1]}</td>
                <td>{item?.currentYear ? "True" : "False"}</td>
                <td>
                  {typeof item?.approvalStatus === "number"
                    ? getStatusText(item?.approvalStatus)
                    : item?.approvalStatus}
                </td>
                <td>{FormatDateTime(item.createdDate, "DD/MM/YYYY")}</td>
                <td>
                  <TableActions hasChildren={true} url={`${item.id}/view`}>
                    {[
                      {
                        name: item?.approvalStatus === 0 ? null : "Edit",
                        action: () =>
                          item?.approvalStatus === 0
                            ? null
                            : handleEdit(item.id),
                      },
                      {
                        name: "View",
                        action: () => handleView(item.id),
                      },
                      {
                        name: item?.approvalStatus === 0 ? null : "Delete",
                        action: () =>
                          item?.approvalStatus === 0
                            ? null
                            : handleOpenDeleteModal(item.id),
                      },
                    ]}
                  </TableActions>
                </td>
              </tr>
            ))}

            {sortedData?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={8} className="text-center">
                  No Appraisal period found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        last_page={data?.responseObject?.totalPages}
        present_page={data?.responseObject?.currentPage}
        totalRows={data?.responseObject?.totalRows}
        pageSize={pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />
      {openCreatModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openCreatModal}
          setIsOpen={handleOpenCreateModale}
        >
          <AppraisalPeriodModal
            isOpen={openCreatModal}
            setIsOpen={setOpenCreatModal}
            onValidate={handleOpenCreateModale}
          />
        </AppModalTemplate>
      )}
      {openModal && selectedId && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openModal}
          setIsOpen={handleModal}
        >
          <EditAppraisalPeriodModal
            isOpen={openModal}
            setIsOpen={setOpenModal}
            onValidate={handleModal}
            id={selectedId}
          />
        </AppModalTemplate>
      )}
      <DeleteItemModal
        isOpen={openDelete}
        handleClose={handleCloseDelete}
        handleDelete={handleDelete}
        headText={"Delete Appraisal Period"}
        infoText={"Are you sure you want to Delete this Appraisal Period?"}
      />
    </PageStyle>
  );
};

export default AppraisalPeriod;
